/**
 * The held foe's receive motions for long moves (ท่ายาว, design docs/design/combat/2026-10-11-long-moves.md: the shared
 * receive set `hit / spin / knock / kneel / fall / recover` (render/rig/receive.ts) plus `lift / pull / downHit / prone`).
 * New here: lifted off the ground and juggled there (`rcvLift`, `rcvVortex`, `rcvHang`, `rcvAirSpin`, `rcvAirHit`),
 * struck down out of the air (`rcvDownHit`, then `rcvFallen` on the ground), yanked in (`rcvPull`), thrown face down
 * (`rcvProne`) and up again from it (`rcvUpProne`).
 *
 * The body's height is the beat's: `lift` channels here are fractions from the height the foe enters the beat at to
 * the height it leaves at (the rules' `h`, metres × `LIFT_PX_PER_M`), so beats chain in the air without a jump;
 * `bump` adds art px (a bounce), `rot` lays the body down (− over backwards, + face down), `turn` whirls the view.
 * `longFoeFrame` picks the motion for a beat from the beats before it (in the air or on the ground, how it went down).
 *
 * Motion ids start with `rcv` (2D only, like the lock forms' receive set).
 */
import type { LimbSpec, PoseRigs } from '../kit';
import type { LongFrame } from './kit';
import { channelAt } from './kit';
import { eased } from '../kit';
import { MOTIONS as GROUND_MOTIONS, RECEIVE_CHANNELS } from '../../rig/receive';

/** Art px a metre of the rules' heights (`h`) is drawn at (the caster's 2.4 m leap = 38 px, the receive knock ~12 px). */
export const LIFT_PX_PER_M = 16;

const FLAT = Math.PI / 2;

export type Poses =
  | 'rcvLiftJolt'
  | 'rcvLiftLimp'
  | 'rcvLiftLimp2'
  | 'rcvAirSnap'
  | 'rcvAirSnap2'
  | 'rcvAirWhirl'
  | 'rcvAirCurl'
  | 'rcvAirCurl2'
  | 'rcvHangA'
  | 'rcvHangB'
  | 'rcvSlam'
  | 'rcvSlamFlat'
  | 'rcvLieTwitch'
  | 'rcvPullJerk'
  | 'rcvPullDrag'
  | 'rcvPullStumble'
  | 'rcvPullCatch'
  | 'rcvProneTip'
  | 'rcvProneFall'
  | 'rcvProneLie'
  | 'rcvProneTwitch'
  | 'rcvProneArms'
  | 'rcvProneKnee';

/** No arms of its own (the receive set uses the shared ones). */
export const ARMS = {} satisfies Record<string, LimbSpec>;

export const POSES: PoseRigs<Poses> = {
  // ── lifted: popped off the feet by an upward blow, then hanging limp in the wind ──
  // The blow: head snapped back, arms flung up, the legs swept from under the body.
  rcvLiftJolt: () => ({ o: 2, tilt: -16, headDx: -1, f1: -3, f2: 3, l1: 6, l2: 8, arm: 'flail', far: 'up', face: 'block', closed: true, sway: -2, hem: 4, flare: 3 }),
  // Limp, curled a little forward, arms and legs dangling.
  rcvLiftLimp: () => ({ o: 4, tilt: 10, headDx: 2, f1: -2, f2: 3, l1: 7, l2: 5, arm: 'hang', far: 'hang', face: 'block', closed: true, sway: 1, hem: 3, flare: 2 }),
  // The other side of the dangle: opened back, the near arm low.
  rcvLiftLimp2: () => ({ o: 3, tilt: 2, headDx: 1, f1: -3, f2: 2, l1: 8, l2: 7, arm: 'low', far: 'hang', face: 'block', closed: true, sway: -1, hem: 4, flare: 3 }),
  // Struck in the air: thrown back over, limbs flung.
  rcvAirSnap: () => ({ o: 2, tilt: -18, headDx: -2, f1: -4, f2: 4, l1: 7, l2: 9, arm: 'flail', far: 'up', face: 'block', closed: true, sway: -2.5, hem: 4, flare: 4 }),
  // Struck the other way: the near arm flung out, the legs swinging under.
  rcvAirSnap2: () => ({ o: 2, tilt: -14, headDx: -2, f1: -5, f2: 3, l1: 9, l2: 6, arm: 'out', far: 'back', face: 'block', closed: true, sway: -2, hem: 4, flare: 4 }),
  // Whirled: spread out by the spin.
  rcvAirWhirl: () => ({ o: 2, tilt: -6, f1: -4, f2: 4, l1: 8, l2: 9, arm: 'out', far: 'up', face: 'block', closed: true, sway: -2.5, hem: 4, flare: 4 }),
  // Whirled: curled in.
  rcvAirCurl: () => ({ o: 5, tilt: 14, headDx: 1, f1: -2, f2: 2, l1: 9, l2: 9, arm: 'guard', far: 'fwd', face: 'block', closed: true, sway: 1, hem: 3, flare: 2 }),
  // Folded over, limp, the knees up.
  rcvAirCurl2: () => ({ o: 6, tilt: 18, headDx: 2, f1: -1, f2: 3, l1: 10, l2: 8, arm: 'hang', far: 'hang', face: 'block', closed: true, sway: 1.5, hem: 3, flare: 2 }),
  // Hung in the vortex: drifting, the head lolled, the robes rising.
  rcvHangA: () => ({ o: 3, tilt: 8, headDx: 2, f1: -2, f2: 2, l1: 6, l2: 4, arm: 'hang', far: 'hang', face: 'block', closed: true, sway: 1, hem: 4, flare: 3 }),
  rcvHangB: () => ({ o: 3, tilt: 5, headDx: 1, f1: -3, f2: 2, l1: 5, l2: 6, arm: 'low', far: 'hang', face: 'block', closed: true, sway: -1, hem: 4, flare: 4 }),
  // ── struck down out of the air ──
  // The chop lands: folded over the blow, arms flung up behind.
  rcvSlam: () => ({ o: 6, tilt: 22, headDx: 2, f1: -4, f2: 4, l1: 4, l2: 6, arm: 'flail', far: 'up', face: 'block', closed: true, sway: 2, hem: 4, flare: 3 }),
  // Laid out straight for the impact (the shot turns it flat).
  rcvSlamFlat: () => ({ o: 1, tilt: -6, headDx: -1, f1: -3, f2: 4, l1: 1, l2: 3, arm: 'out', far: 'up', face: 'block', closed: true, sway: -1, hem: 2, flare: 2 }),
  // On the back, a knee drawn up, the head rolled.
  rcvLieTwitch: () => ({ o: 1, tilt: -4, headDx: 1, f1: -2, f2: 4, l2: 4, arm: 'out', far: 'up', face: 'block', closed: true, sway: 0.5 }),
  // ── pulled in ──
  // Yanked toward the puller: pitched forward, the arms trailing, the back foot dragging.
  rcvPullJerk: () => ({ o: 4, lean: 3, tilt: 22, headDx: 2, f1: -11, f2: 2, l2: 3, arm: 'back', far: 'back', face: 'block', closed: true, sway: 2.5, hem: 2, flare: 1 }),
  // Dragged: the body still ahead of the feet.
  rcvPullDrag: () => ({ o: 5, lean: 3, tilt: 20, headDx: 2, f1: -9, f2: 4, l1: 2, arm: 'back', far: 'back', face: 'block', sway: 2, hem: 2, flare: 1 }),
  // A stumbling step to catch the fall.
  rcvPullStumble: () => ({ o: 6, lean: 2, tilt: 16, headDx: 1, f1: -4, f2: 8, l1: 3, arm: 'flail', far: 'fwd', face: 'block', sway: 1.5, hem: 1 }),
  // Caught off balance, bent forward, hands low.
  rcvPullCatch: () => ({ o: 7, lean: 1, tilt: 18, headDx: 1, f1: -6, f2: 7, arm: 'hang', far: 'fwd', face: 'block', sway: 0.5 }),
  // ── face down ──
  // Tipping forward off the feet, arms going out to break the fall.
  rcvProneTip: () => ({ o: 4, lean: 3, tilt: 26, headDx: 2, f1: -8, f2: 4, l1: 4, arm: 'forward', far: 'fwd', face: 'block', closed: true, sway: 1.5, hem: 1 }),
  // Falling flat, the arms thrown ahead of the head.
  rcvProneFall: () => ({ o: 1, tilt: 8, headDx: 1, f1: -5, f2: 2, l1: 4, arm: 'raise', far: 'up', face: 'block', closed: true, sway: 1.5, hem: 2, flare: 1 }),
  // Lying face down (the shot turns it flat): straight, the arms ahead of the head.
  rcvProneLie: () => ({ o: 0, tilt: 2, f1: -2, f2: 2, arm: 'raise', far: 'up', face: 'block', closed: true }),
  rcvProneTwitch: () => ({ o: 1, tilt: 4, headDx: 1, f1: -3, f2: 2, l1: 2, arm: 'raise', far: 'up', face: 'block', closed: true, sway: 0.3 }),
  // Pushing up on the arms.
  rcvProneArms: () => ({ o: 6, tilt: 22, headDx: 1, f1: -6, f2: 3, arm: 'low', far: 'hang', face: 'block', sway: 0.5 }),
  // A knee drawn under the body.
  rcvProneKnee: () => ({ o: 14, tilt: 28, headDx: 1, f1: -8, f2: 6, arm: 'low', far: 'hang', face: 'block', sway: 0.5 }),
};

/** The motions (keys counted in the comments). */
export const MOTIONS = [
  /** Popped off the feet and lifted, then hanging limp (8). */
  {
    id: 'rcvLift',
    keys: ['rcvStagger', 'rcvLiftJolt', 'rcvAirSnap', 'rcvLiftLimp2', 'rcvLiftLimp', 'rcvHangB', 'rcvHangA', 'rcvLiftLimp'],
    ease: ['out', 'out', 'inOut', 'inOut', 'inOut', 'inOut', 'inOut'],
  },
  /** Carried up the vortex, rocked by four cuts (11). */
  {
    id: 'rcvVortex',
    keys: ['rcvLiftLimp', 'rcvAirSnap', 'rcvAirCurl', 'rcvAirSnap2', 'rcvAirWhirl', 'rcvAirSnap', 'rcvAirCurl2', 'rcvAirSnap2', 'rcvLiftLimp2', 'rcvHangA', 'rcvHangB'],
    ease: ['out', 'inOut', 'out', 'inOut', 'out', 'inOut', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Hung in the air, drifting: one slow sway over the beat (a loop of 2). */
  {
    id: 'rcvHang',
    keys: ['rcvHangA', 'rcvHangB'],
    ease: ['inOut', 'inOut'],
    loop: true,
  },
  /** Whirled round twice in the air by a passing cut (8; the view turns). */
  {
    id: 'rcvAirSpin',
    keys: ['rcvLiftLimp', 'rcvAirSnap', 'rcvAirWhirl', 'rcvAirCurl', 'rcvAirSnap2', 'rcvAirCurl2', 'rcvLiftLimp2', 'rcvLiftLimp'],
    ease: ['out', 'linear', 'linear', 'linear', 'linear', 'out', 'inOut'],
  },
  /** Rocked by two blows in the air (8). */
  {
    id: 'rcvAirHit',
    keys: ['rcvLiftLimp', 'rcvAirSnap', 'rcvAirCurl', 'rcvLiftLimp2', 'rcvAirSnap2', 'rcvAirCurl2', 'rcvHangA', 'rcvLiftLimp'],
    ease: ['out', 'out', 'inOut', 'out', 'out', 'inOut', 'inOut'],
  },
  /** Struck down out of the air onto the back: folded by the blow, laid out, the impact, a bounce, lying (9). */
  {
    id: 'rcvDownHit',
    keys: ['rcvLiftLimp', 'rcvSlam', 'rcvSlam', 'rcvSlamFlat', 'rcvImpact', 'rcvBounce', 'rcvImpact', 'rcvLie', 'rcvLie'],
    ease: ['out', 'in', 'in', 'in', 'out', 'in', 'inOut', 'inOut'],
  },
  /** Down on the back after the slam: lying, a twitch, still (4). */
  {
    id: 'rcvFallen',
    keys: ['rcvLie', 'rcvLieTwitch', 'rcvLie', 'rcvLie'],
    ease: ['inOut', 'inOut', 'inOut'],
  },
  /** Yanked in toward the puller, dragged, a stumbling step, caught off balance (8). */
  {
    id: 'rcvPull',
    keys: ['rcvStagger', 'rcvPullJerk', 'rcvPullDrag', 'rcvPullDrag', 'rcvPullStumble', 'rcvPullCatch', 'rcvSag', 'rcvStagger'],
    ease: ['out', 'linear', 'inOut', 'out', 'out', 'inOut', 'inOut'],
  },
  /** Over forwards onto the face: tipping, falling flat, the impact, lying (8). */
  {
    id: 'rcvProne',
    keys: ['rcvStagger', 'rcvProneTip', 'rcvProneFall', 'rcvProneFall', 'rcvProneLie', 'rcvProneTwitch', 'rcvProneLie', 'rcvProneLie'],
    ease: ['out', 'in', 'in', 'out', 'in', 'inOut', 'inOut'],
  },
  /** Up from the face: pushed up on the arms, a knee under, kneeling, up, set (9). */
  {
    id: 'rcvUpProne',
    keys: ['rcvProneLie', 'rcvProneArms', 'rcvProneKnee', 'rcvKneelLook', 'rcvPush', 'rcvRiseHalf', 'rcvGather', 'rcvSet', 'ready'],
    ease: ['inOut', 'inOut', 'out', 'inOut', 'out', 'inOut', 'inOut', 'inOut'],
  },
] as const;

export type LongReceiveId = (typeof MOTIONS)[number]['id'];

/** Beside the keys: `lift` fractions from the entering height to the leaving one, `bump` art px, `rot`, `turn`. */
interface Ch {
  lift?: readonly number[];
  bump?: readonly number[];
  rot?: readonly number[];
  turn?: readonly number[];
}
export const CHANNELS: Readonly<Partial<Record<LongReceiveId, Ch>>> = {
  rcvLift: { lift: [0, 0.15, 0.55, 0.85, 1, 1, 1, 1], bump: [0, 0, 0, 0, 0, 1, 0, 0] },
  rcvVortex: { lift: [0, 0.15, 0.25, 0.42, 0.52, 0.68, 0.78, 0.92, 1, 1, 1], turn: [0, 1, 2, 3, 4, 5, 6, 7, 8, 8, 8] },
  rcvHang: { lift: [1, 1], bump: [0, 1] },
  rcvAirSpin: { lift: [1, 1, 1, 1, 1, 1, 1, 1], bump: [0, 2, 3, 3, 2, 1, 0, 0], turn: [0, 2, 5, 8, 11, 14, 16, 16] },
  rcvAirHit: { lift: [1, 1, 1, 1, 1, 1, 1, 1], bump: [0, 2, 1, 0, 2, 1, 0, 0] },
  rcvDownHit: {
    lift: [0, 0, 0.3, 0.8, 1, 1, 1, 1, 1],
    bump: [0, 2, 0, 0, 0, 3, 0, 0, 0],
    rot: [0, 0.15, -0.35, -1.1, -FLAT, -FLAT + 0.1, -FLAT, -FLAT, -FLAT],
  },
  rcvFallen: { rot: [-FLAT, -FLAT, -FLAT, -FLAT], bump: [0, 1, 0, 0] },
  rcvProne: { rot: [0, 0.12, 0.6, 1.2, FLAT, FLAT - 0.08, FLAT, FLAT], bump: [0, 0, 1, 2, 0, 2, 0, 0] },
  rcvUpProne: { rot: [FLAT, 1.05, 0.55, 0, 0, 0, 0, 0, 0] },
};

/** The beat data a foe frame needs (the rules' `LongBeat`: how the foe reacts, how high, how many blows). */
export interface FoeBeat {
  readonly foe: string;
  readonly h?: number;
  readonly hits?: number;
  readonly move?: { readonly kind: string };
}

/** The ground receive motions (render/rig/receive.ts) by beat. */
const GROUND: Readonly<Record<string, string>> = {
  hit: 'rcvHit',
  spin: 'rcvSpin',
  knock: 'rcvKnock',
  kneel: 'rcvKneel',
  fall: 'rcvFall',
};

/** Height (m) a foe leaves beat `b` at, entering at `h0`. */
function heightAfter(b: FoeBeat, h0: number): number {
  if (b.foe === 'lift') return b.h ?? h0;
  if (b.foe === 'spin' || b.foe === 'hit') return h0;
  return 0;
}

/** Which motion the foe plays in beat `i`, and the heights it enters and leaves at (m). */
export function foeMotionOf(beats: readonly FoeBeat[], i: number): { id: string; h0: number; h1: number } {
  let h = 0;
  let downFrom = '';
  for (let j = 0; j < i; j++) {
    const b = beats[j]!;
    if (b.foe !== 'recover') downFrom = b.foe === 'knock' && h > 0 ? 'downHit' : b.foe;
    h = heightAfter(b, h);
  }
  const b = beats[i]!;
  const h1 = heightAfter(b, h);
  const air = h > 0;
  switch (b.foe) {
    case 'lift':
      if (air && Math.abs(h1 - h) < 1e-6) return { id: 'rcvHang', h0: h, h1 };
      return { id: (b.hits ?? 0) > 1 ? 'rcvVortex' : 'rcvLift', h0: h, h1 };
    case 'spin':
      return air ? { id: 'rcvAirSpin', h0: h, h1 } : { id: 'rcvSpin', h0: 0, h1: 0 };
    case 'hit':
      return air ? { id: 'rcvAirHit', h0: h, h1 } : { id: 'rcvHit', h0: 0, h1: 0 };
    case 'knock':
      return air ? { id: 'rcvDownHit', h0: h, h1: 0 } : { id: 'rcvKnock', h0: 0, h1: 0 };
    case 'downHit':
      return { id: 'rcvDownHit', h0: h, h1: 0 };
    case 'fall':
      return { id: downFrom === 'downHit' ? 'rcvFallen' : 'rcvFall', h0: h, h1: 0 };
    case 'prone':
      return { id: 'rcvProne', h0: h, h1: 0 };
    case 'pull':
      return { id: 'rcvPull', h0: 0, h1: 0 };
    case 'recover':
      return {
        id:
          downFrom === 'prone'
            ? 'rcvUpProne'
            : downFrom === 'fall' || downFrom === 'downHit'
              ? 'rcvUpKip'
              : downFrom === 'kneel'
                ? 'rcvUpKneel'
                : 'rcvUp',
        h0: 0,
        h1: 0,
      };
    default:
      return { id: GROUND[b.foe] ?? 'rcvHit', h0: 0, h1: 0 };
  }
}

/** The held foe's frame in beat `i`, `k` (0–1) of the way through it. Motions of the old set carry their own channels
 * (render/rig/receivePlay.ts); these are drawn on the ground. */
export function longFoeFrame(beats: readonly FoeBeat[], i: number, k: number): LongFrame {
  const { id, h0, h1 } = foeMotionOf(beats, i);
  const u = Math.max(0, Math.min(1, k));
  const g = GROUND_MOTIONS.find((x) => x.id === id);
  if (g) {
    // The lock forms' receive set: its own channels (laid down, lifted, whirled), on the ground.
    const c = RECEIVE_CHANNELS[g.id];
    const at = (v: readonly number[] | undefined) => (v ? channelAt(v, g.ease, u) : 0);
    return { id, u, lift: at(c?.lift), rot: at(c?.rot), turn: Math.round(at(c?.turn)) };
  }
  const m = MOTIONS.find((x) => x.id === id);
  const ch = m ? CHANNELS[m.id] : undefined;
  const ease = m?.ease ?? [];
  const f = ch?.lift ? channelAt(ch.lift, ease, u) : h0 === h1 ? 1 : eased('inOut', u);
  const lift = (h0 + (h1 - h0) * f) * LIFT_PX_PER_M + (ch?.bump ? channelAt(ch.bump, ease, u) : 0);
  return {
    id,
    u,
    lift: Math.max(0, lift),
    rot: ch?.rot ? channelAt(ch.rot, ease, u) : 0,
    turn: ch?.turn ? Math.round(channelAt(ch.turn, ease, u)) : 0,
  };
}
